import {
    FaExclamationCircle, FaBox, FaFileAlt, FaPlane, FaCube, FaHeadset, FaBuilding,
    FaQuestionCircle, FaEnvelope, FaLock, FaCheckCircle, FaUserShield, FaShieldAlt,
    FaBriefcase, FaIdCard, FaGlobe, FaCertificate, FaGem, FaMicrochip, FaPaw,
    FaNotesMedical, FaThermometerHalf, FaBoxOpen, FaIndustry, FaPalette, FaMedkit,
    FaFileSignature, FaSearchDollar
} from "react-icons/fa";
import React from 'react';

/**
 * Global Search Data Index
 * This centralizes all searchable content across the website.
 * Each entry handles both Spanish and English versions with appropriate routes.
 */
export const searchData = [
    // --- TOP LEVEL / UTILITIES ---
    {
        id: "home",
        category: { es: "General", us: "General" },
        title: { es: "Página de Inicio", us: "Home Page" },
        description: { es: "Página principal de Aeroméxico Cargo.", us: "Aeroméxico Cargo main page." },
        link: { es: "/", us: "/" },
        tags: { es: ["inicio", "home", "principal"], us: ["home", "main"] },
        icon: <FaPlane />
    },
    {
        id: "tracking",
        category: { es: "Seguimiento", us: "Tracking" },
        title: { es: "Rastreo de Guía Aérea", us: "Air Waybill Tracking" },
        description: { es: "Sigue el estado de tu carga en tiempo real.", us: "Track your cargo's status in real-time." },
        link: { es: "/seguimiento", us: "/track" },
        tags: { es: ["rastreo", "guía", "tracking", "donde esta"], us: ["tracking", "waybill", "trace"] },
        icon: <FaBox />
    },
    {
        id: "quoting",
        category: { es: "Cotización", us: "Quoting" },
        title: { es: "Cotización de Embarques", us: "Shipment Quoting" },
        description: { es: "Obtén tarifas y presupuestos para tus envíos.", us: "Get rates and quotes for your shipments." },
        link: { es: "/cotizar", us: "/quote" },
        tags: { es: ["cotizar", "precio", "tarifa", "cuanto cuesta", "presupuesto"], us: ["quote", "price", "rate", "cost"] },
        icon: <FaSearchDollar />
    },

    // --- PREPARE SHIPMENT ---
    {
        id: "restrictions",
        category: { es: "Preparación de Envío", us: "Prepare Shipment" },
        title: { es: "Restricciones de Envíos Internacionales", us: "International Shipping Restrictions" },
        description: { es: "Materiales peligrosos, sustancias prohibidas y documentación.", us: "Hazardous materials, prohibited substances, and documentation." },
        link: { es: "/envios/restricciones", us: "/shipments/restrictions" },
        tags: { es: ["restricciones", "prohibido", "peligroso", "aduana", "normas"], us: ["restrictions", "prohibited", "hazardous", "customs"] },
        icon: <FaExclamationCircle />
    },
    {
        id: "general_cargo",
        category: { es: "Preparación de Envío", us: "Prepare Shipment" },
        title: { es: "Carga General", us: "General Cargo" },
        description: { es: "Información sobre transporte de mercancías generales.", us: "Information on transporting general merchandise." },
        link: { es: "/envios/carga-general", us: "/shipments/genaral-cargo" },
        tags: { es: ["carga general", "mercancia", "empaque", "embalaje"], us: ["general cargo", "merchandise", "packaging"] },
        icon: <FaCube />
    },
    {
        id: "label_generator",
        category: { es: "Preparación de Envío", us: "Prepare Shipment" },
        title: { es: "Generador de Etiquetas", us: "Label Generator" },
        description: { es: "Crea y descarga etiquetas para tus embarques de forma fácil.", us: "Easily create and download labels for your shipments." },
        link: { es: "/envios/generador-de-etiquetas", us: "/shipments/label-generator" },
        tags: { es: ["etiquetas", "generador", "imprimir", "marcado"], us: ["labels", "generator", "print", "marking"] },
        icon: <FaFileAlt />
    },

    // --- SERVICES NATIONAL ---
    {
        id: "national_main",
        category: { es: "Servicios", us: "Services" },
        title: { es: "Servicios Nacionales", us: "National Services" },
        description: { es: "Soluciones de transporte dentro de México.", us: "Transport solutions within Mexico." },
        link: { es: "/servicios/nacionales", us: "/services/nationals" },
        tags: { es: ["nacional", "mexico", "servicios locales"], us: ["national", "mexico", "local"] },
        icon: <FaGlobe />
    },
    {
        id: "service_gold",
        category: { es: "Servicios Nacionales", us: "National Services" },
        title: { es: "Servicio Gold", us: "Gold Service" },
        description: { es: "Servicio premium para entregas urgentes prioritarias.", us: "Premium service for urgent priority deliveries." },
        link: { es: "/servicios/nacionales/gold", us: "/services/nationals/gold" },
        tags: { es: ["gold", "urgente", "prioridad", "premium"], us: ["gold", "urgent", "priority", "premium"] },
        icon: <FaGem />
    },
    {
        id: "service_12_12",
        category: { es: "Servicios Nacionales", us: "National Services" },
        title: { es: "Servicio 12+12", us: "12+12 Service" },
        description: { es: "Carga estándar con tiempos de entrega garantizados.", us: "Standard cargo with guaranteed delivery times." },
        link: { es: "/servicios/nacionales/doce+doce", us: "/services/nationals/twelve+twelve" },
        tags: { es: ["12+12", "estándar", "garantizado"], us: ["12+12", "standard", "guaranteed"] },
        icon: <FaCheckCircle />
    },
    {
        id: "service_courier",
        category: { es: "Servicios Nacionales", us: "National Services" },
        title: { es: "Mensajería (Courier)", us: "Courier Service" },
        description: { es: "Envíos rápidos de sobres y paquetes pequeños.", us: "Fast shipping of envelopes and small packages." },
        link: { es: "/servicios/nacionales/mensajeria", us: "/services/nationals/courier" },
        tags: { es: ["courier", "mensajeria", "paqueteria", "sobres"], us: ["courier", "messaging", "packages", "envelopes"] },
        icon: <FaEnvelope />
    },
    {
        id: "service_perishables",
        category: { es: "Servicios Nacionales", us: "National Services" },
        title: { es: "Perecederos", us: "Perishables" },
        description: { es: "Productos de temperatura controlada y alimentos.", us: "Temperature-controlled products and food." },
        link: { es: "/servicios/nacionales/perecederos", us: "/services/nationals/perishable-goods" },
        tags: { es: ["frio", "comida", "alimentos", "fresco"], us: ["cold", "food", "fresh", "chilled"] },
        icon: <FaThermometerHalf />
    },
    {
        id: "service_high_value",
        category: { es: "Servicios Nacionales", us: "National Services" },
        title: { es: "Alto Valor", us: "High Value" },
        description: { es: "Seguridad extrema para mercancías valiosas.", us: "Extreme security for high-value merchandise." },
        link: { es: "/servicios/nacionales/alto-valor", us: "/services/nationals/high-value" },
        tags: { es: ["joyas", "dinero", "valioso", "seguridad"], us: ["money", "jewelry", "valuable", "security"] },
        icon: <FaUserShield />
    },
    {
        id: "service_high_tech",
        category: { es: "Servicios Nacionales", us: "National Services" },
        title: { es: "Alta Tecnología", us: "High Technology" },
        description: { es: "Manejo delicado para equipos electrónicos avanzados.", us: "Delicate handling for advanced electronic equipment." },
        link: { es: "/servicios/nacionales/alta-tecnologia", us: "/services/nationals/high-technology" },
        tags: { es: ["tecnología", "chips", "electrónicos", "delicado"], us: ["technology", "chips", "electronics", "delicate"] },
        icon: <FaMicrochip />
    },
    {
        id: "service_live_animals",
        category: { es: "Servicios Nacionales", us: "National Services" },
        title: { es: "Animales Vivos", us: "Live Animals" },
        description: { es: "Transporte seguro y ético para mascotas y animales.", us: "Safe and ethical transport for pets and animals." },
        link: { es: "/servicios/nacionales/animales-vivos", us: "/services/nationals/live-animals" },
        tags: { es: ["mascotas", "perros", "gatos", "caballos", "etología"], us: ["pets", "dogs", "cats", "animals", "horses"] },
        icon: <FaPaw />
    },
    {
        id: "service_human_remains",
        category: { es: "Servicios Nacionales", us: "National Services" },
        title: { es: "Restos Humanos", us: "Human Remains" },
        description: { es: "Atención digna y respetuosa para traslados funerarios.", us: "Dignified and respectful attention for funeral transfers." },
        link: { es: "/servicios/nacionales/restos-humanos", us: "/services/nationals/human-remains" },
        tags: { es: ["funerario", "féretro", "cenizas", "ataúd"], us: ["funeral", "ashes", "coffin", "urn"] },
        icon: <FaBuilding />
    },
    {
        id: "service_pharma_xps",
        category: { es: "Servicios Nacionales", us: "National Services" },
        title: { es: "Pharma Express", us: "Pharma Express" },
        description: { es: "Solución de enfriamiento pasivo para medicamentos urgentes.", us: "Passive cooling solution for urgent medicines." },
        link: { es: "/servicios/nacionales/pharma-express", us: "/services/nationals/pharma-xps" },
        tags: { es: ["pharma", "medicinas", "vacunas", "biológicos"], us: ["pharma", "medicines", "vaccines", "biologicals"] },
        icon: <FaMedkit />
    },
    {
        id: "service_pharma_std",
        category: { es: "Servicios Nacionales", us: "National Services" },
        title: { es: "Pharma Estandar", us: "Pharma Standard" },
        description: { es: "Manejo de productos farmacéuticos no críticos.", us: "Handling of non-critical pharmaceutical products." },
        link: { es: "/servicios/nacionales/pharma-estandar", us: "/services/nationals/pharma-std" },
        tags: { es: ["pharma", "estandar", "medicinas", "salud"], us: ["pharma", "standard", "medicines", "health"] },
        icon: <FaBriefcase />
    },
    {
        id: "service_pouch",
        category: { es: "Servicios Nacionales", us: "National Services" },
        title: { es: "Valija", us: "Pouch" },
        description: { es: "Transporte de documentos internos y valijas empresariales.", us: "Transport of internal documents and business pouches." },
        link: { es: "/servicios/nacionales/valija", us: "/services/nationals/pouch" },
        tags: { es: ["valija", "sobres", "documentos", "empresa"], us: ["pouch", "envelopes", "documents", "business"] },
        icon: <FaBoxOpen />
    },
    {
        id: "service_hazardous",
        category: { es: "Servicios Nacionales", us: "National Services" },
        title: { es: "Mercancía Peligrosa", us: "Hazardous Goods" },
        description: { es: "Manejo certificado de sustancias peligrosas.", us: "Certified handling of hazardous substances." },
        link: { es: "/servicios/nacionales/mercancia-peligrosa", us: "/services/nationals/hazardous-goods" },
        tags: { es: ["peligrosa", "dgr", "baterías", "químicos", "explosivos"], us: ["hazardous", "dgr", "dangerous", "chemicals"] },
        icon: <FaExclamationCircle />
    },
    {
        id: "service_industry",
        category: { es: "Servicios Nacionales", us: "National Services" },
        title: { es: "Servicios para la Industria", us: "Industry Services" },
        description: { es: "Soluciones logísticas para AOG y manufactura pesada.", us: "Logistics solutions for AOG and heavy manufacturing." },
        link: { es: "/servicios/nacionales/servicios-para-la-industria", us: "/services/nationals/industry-services" },
        tags: { es: ["industria", "automotriz", "aeronáutica", "aog", "repuestos"], us: ["industry", "automotive", "aeronautic", "aog", "spare parts"] },
        icon: <FaIndustry />
    },

    // --- SERVICES INTERNATIONAL ---
    {
        id: "international_main",
        category: { es: "Servicios", us: "Services" },
        title: { es: "Servicios Internacionales", us: "International Services" },
        description: { es: "Soluciones globales de importación y exportación.", us: "Global import and export solutions." },
        link: { es: "/servicios/internacional", us: "/services/international" },
        tags: { es: ["internacional", "asia", "europa", "usa", "mundo"], us: ["international", "asia", "europe", "usa", "world"] },
        icon: <FaGlobe />
    },
    {
        id: "int_express",
        category: { es: "Servicios Internacionales", us: "International Services" },
        title: { es: "Express Internacional", us: "International Express" },
        description: { es: "Rapidez prioritaria para tus envíos críticos al mundo.", us: "Priority speed for your critical worldwide shipments." },
        link: { es: "/servicios/internacional/express", us: "/services/international/express" },
        tags: { es: ["express", "rápido", "urgente", "global"], us: ["express", "fast", "urgent", "global"] },
        icon: <FaPlane />
    },
    {
        id: "int_general",
        category: { es: "Servicios Internacionales", us: "International Services" },
        title: { es: "General Internacional", us: "International General" },
        description: { es: "Transporte balanceado para carga comercial global.", us: "Balanced transport for global commercial cargo." },
        link: { es: "/servicios/internacional/general", us: "/services/international/general" },
        tags: { es: ["general", "comercio", "carga", "estándar"], us: ["general", "trade", "cargo", "standard"] },
        icon: <FaCube />
    },
    {
        id: "int_specialized",
        category: { es: "Servicios Internacionales", us: "International Services" },
        title: { es: "Carga Especializada", us: "Specialized Cargo" },
        description: { es: "Manejo experto para mercancías con requerimientos especiales.", us: "Expert handling for merchandise with special requirements." },
        link: { es: "/servicios/internacional/especializado", us: "/services/international/specialized" },
        tags: { es: ["especializado", "cuidados", "especial"], us: ["specialized", "care", "special"] },
        icon: <FaBriefcase />
    },
    {
        id: "specialized_art",
        category: { es: "Servicios Internacionales", us: "International Services" },
        title: { es: "Obras de Arte", us: "Works of Art" },
        description: { es: "Logística delicada para piezas de arte y cultura.", us: "Delicate logistics for art and culture pieces." },
        link: { es: "/servicios/internacional/obras-de-arte", us: "/services/international/specialized-art" },
        tags: { es: ["arte", "cuadro", "museo", "escultura", "valioso"], us: ["art", "painting", "museum", "sculpture", "valuable"] },
        icon: <FaPalette />
    },
    {
        id: "specialized_care",
        category: { es: "Servicios Internacionales", us: "International Services" },
        title: { es: "Cuidado de la Salud", us: "Healthcare" },
        description: { es: "Transporte de equipos médicos y suministros de salud.", us: "Transport of medical equipment and health supplies." },
        link: { es: "/servicios/internacional/cuidado-de-la-salud", us: "/services/international/specialized-care" },
        tags: { es: ["salud", "médico", "hospital", "insumos"], us: ["health", "medical", "hospital", "supplies"] },
        icon: <FaNotesMedical />
    },
    {
        id: "specialized_hazardous",
        category: { es: "Servicios Internacionales", us: "International Services" },
        title: { es: "Mercancías Peligrosas", us: "Dangerous Goods" },
        description: { es: "Cumplimiento IATA para transporte de sustancias riesgosas.", us: "IATA compliance for transporting risky substances." },
        link: { es: "/servicios/internacional/mercancias-peligrosas", us: "/services/international/specialized-dgr" },
        tags: { es: ["peligrosa", "dgr", "baterías", "químicos"], us: ["dangerous", "dgr", "hazardous", "chemicals"] },
        icon: <FaExclamationCircle />
    },
    {
        id: "specialized_fresh",
        category: { es: "Servicios Internacionales", us: "International Services" },
        title: { es: "Temperatura Controlada", us: "Temperature Controlled" },
        description: { es: "Cadena de frío para perecederos internacionales.", us: "Cold chain for international perishables." },
        link: { es: "/servicios/internacional/temperatura-controlada", us: "/services/international/specialized-fresh" },
        tags: { es: ["frio", "fresco", "comida", "helado", "flores"], us: ["cold", "fresh", "food", "frozen", "flowers"] },
        icon: <FaThermometerHalf />
    },
    {
        id: "specialized_live",
        category: { es: "Servicios Internacionales", us: "International Services" },
        title: { es: "Cuidado de Animales Vivos", us: "Specialized Live Animals" },
        description: { es: "Transporte especializado para bienestar animal global.", us: "Specialized transport for global animal welfare." },
        link: { es: "/servicios/internacional/cuidado-especializado-para-animales-vivos", us: "/services/international/specialized-live" },
        tags: { es: ["animales", "pets", "mascotas", "caballos", "etología"], us: ["animals", "pets", "pets", "horses", "veterinary"] },
        icon: <FaPaw />
    },
    {
        id: "specialized_pharma",
        category: { es: "Servicios Internacionales", us: "International Services" },
        title: { es: "Pharma Especializado", us: "Specialized Pharma" },
        description: { es: "Certificación CEIV Pharma para transporte farmacéutico.", us: "CEIV Pharma certification for pharma transport." },
        link: { es: "/servicios/internacional/pharma-especializado", us: "/services/international/specialized-pharma" },
        tags: { es: ["pharma", "medicinas", "vacunas", "biotecnología"], us: ["pharma", "medicines", "vaccines", "biotech"] },
        icon: <FaMedkit />
    },
    {
        id: "specialized_safe",
        category: { es: "Servicios Internacionales", us: "International Services" },
        title: { es: "Carga Asegurada", us: "Insured Cargo" },
        description: { es: "Protección financiera y física para tu carga global.", us: "Financial and physical protection for your global cargo." },
        link: { es: "/servicios/internacional/carga-asegurada", us: "/services/international/specialized-safe" },
        tags: { es: ["seguro", "cobertura", "protegido"], us: ["insurance", "coverage", "protected"] },
        icon: <FaLock />
    },
    {
        id: "int_customized",
        category: { es: "Servicios Internacionales", us: "International Services" },
        title: { es: "Customizado", us: "Customized" },
        description: { es: "Soluciones de transporte diseñadas a tu medida.", us: "Transport solutions tailored to your needs." },
        link: { es: "/servicios/internacional/customizado", us: "/services/international/customized" },
        tags: { es: ["medida", "charter", "personalizado"], us: ["tailored", "charter", "custom"] },
        icon: <FaBuilding />
    },
    {
        id: "additional_services",
        category: { es: "Servicios", us: "Services" },
        title: { es: "Servicios Adicionales", us: "Additional Services" },
        description: { es: "Servicios de valor agregado para complementar tu logística.", us: "Value-added services to complement your logistics." },
        link: { es: "/servicios/servicios-adicionales", us: "/services/additional-services" },
        tags: { es: ["adicional", "valor agregado", "almacén", "seguro"], us: ["additional", "value added", "warehousing"] },
        icon: <FaBoxOpen />
    },

    // --- STATIONS ---
    {
        id: "stations_national",
        category: { es: "Estaciones", us: "Stations" },
        title: { es: "Estaciones Nacionales", us: "National Stations" },
        description: { es: "Nuestras oficinas y puntos de atención en México.", us: "Our offices and service points in Mexico." },
        link: { es: "/estaciones/nacionales", us: "/stations/nationals" },
        tags: { es: ["oficinas", "sucursales", "donde", "ubicaciones", "mexico"], us: ["stations", "offices", "locations", "mexico"] },
        icon: <FaBuilding />
    },
    {
        id: "stations_international",
        category: { es: "Estaciones", us: "Stations" },
        title: { es: "Estaciones Internacionales", us: "International Stations" },
        description: { es: "Ubicaciones estratégicas en el extranjero.", us: "Strategic locations abroad." },
        link: { es: "/estaciones/internacional", us: "/stations/international" },
        tags: { es: ["mundo", "aeropuertos", "usa", "europa", "latam"], us: ["world", "airports", "usa", "europe", "latam"] },
        icon: <FaGlobe />
    },
    {
        id: "bonded_warehouse",
        category: { es: "Estaciones", us: "Stations" },
        title: { es: "Almacén Fiscalizado", us: "Bonded Warehouse" },
        description: { es: "Servicios de almacenamiento de aduana seguros.", us: "Secure customs storage services." },
        link: { es: "/estaciones/almacenes-en-mexico", us: "/stations/warehouses-in-mexico" },
        tags: { es: ["aduana", "almacén", "importación", "fiscalizado"], us: ["customs", "warehouse", "import", "bonded"] },
        icon: <FaBuilding />
    },

    // --- FLEET ---
    {
        id: "fleet_main",
        category: { es: "Flota", us: "Fleet" },
        title: { es: "Nuestra Flota", us: "Our Fleet" },
        description: { es: "Aeronaves para transporte de carga seguro.", us: "Aircraft for secure cargo transport." },
        link: { es: "/nosotros/flota", us: "/about-us/fleet" },
        tags: { es: ["aviones", "capacidad", "boeing", "embraer"], us: ["planes", "capacity", "boeing", "embraer"] },
        icon: <FaPlane />
    },
    {
        id: "fleet_erj_190",
        category: { es: "Flota", us: "Fleet" },
        title: { es: "Embraer 190 (ERJ-190)", us: "Embraer 190 (ERJ-190)" },
        description: { es: "Eficiencia regional para carga nacional.", us: "Regional efficiency for national cargo." },
        link: { es: "/nosotros/flota/erj-190", us: "/about-us/fleet/erj-190" },
        tags: { es: ["erj-190", "compañía", "regional", "corto alcance"], us: ["erj-190", "regional", "short range"] },
        icon: <FaPlane />
    },
    {
        id: "fleet_b737_700",
        category: { es: "Flota", us: "Fleet" },
        title: { es: "Boeing 737-700", us: "Boeing 737-700" },
        description: { es: "Versatilidad y rapidez para rutas nacionales y cortas.", us: "Versatility and speed for national and short routes." },
        link: { es: "/nosotros/flota/b737-700", us: "/about-us/fleet/b737-700" },
        tags: { es: ["b737", "corto alcance", "versátil"], us: ["b737", "short range", "versatile"] },
        icon: <FaPlane />
    },
    {
        id: "fleet_b737_800",
        category: { es: "Flota", us: "Fleet" },
        title: { es: "Boeing 737-800", us: "Boeing 737-800" },
        description: { es: "Eficiencia comprobada para transporte de carga media.", us: "Proven efficiency for medium cargo transport." },
        link: { es: "/nosotros/flota/b737-800", us: "/about-us/fleet/b737-800" },
        tags: { es: ["b737-800", "eficiencia", "carga"], us: ["b737-800", "efficiency", "cargo"] },
        icon: <FaPlane />
    },
    {
        id: "fleet_b787_8",
        category: { es: "Flota", us: "Fleet" },
        title: { es: "Boeing 787-8 Dreamliner", us: "Boeing 787-8 Dreamliner" },
        description: { es: "Doble pasillo para carga internacional masiva.", us: "Double aisle for massive international cargo." },
        link: { es: "/nosotros/flota/b787-8", us: "/about-us/fleet/b787-8" },
        tags: { es: ["b787", "dreamliner", "gran capacidad", "internacional"], us: ["b787", "dreamliner", "high capacity"] },
        icon: <FaPlane />
    },
    {
        id: "fleet_b787_9",
        category: { es: "Flota", us: "Fleet" },
        title: { es: "Boeing 787-9 Dreamliner", us: "Boeing 787-9 Dreamliner" },
        description: { es: "Más capacidad y alcance para envíos globales críticos.", us: "More capacity and range for critical global shipments." },
        link: { es: "/nosotros/flota/b787-9", us: "/about-us/fleet/b787-9" },
        tags: { es: ["787-9", " dreamliner", "máxima capacidad"], us: ["787-9", "dreamliner", "max capacity"] },
        icon: <FaPlane />
    },

    // --- ALERTS ---
    {
        id: "alerts_main",
        category: { es: "Alertas", us: "Alerts" },
        title: { es: "Alertas y Noticias", us: "Alerts and News" },
        description: { es: "Avisos operativos y regulaciones importantes.", us: "Operational notices and important regulations." },
        link: { es: "/alertas", us: "/alerts" },
        tags: { es: ["avisos", "comunicados", "importante"], us: ["notices", "statements", "important"] },
        icon: <FaExclamationCircle />
    },
    {
        id: "acas_protocol",
        category: { es: "Alertas", us: "Alerts" },
        title: { es: "Protocolo ACAS", us: "ACAS Protocol" },
        description: { es: "Medidas de seguridad para embarques a Estados Unidos.", us: "Security measures for shipments to the United States." },
        link: { es: "/alertas/protocolo-acas", us: "/alerts/acas-protocol" },
        tags: { es: ["acas", "usa", "seguridad", "vuelos"], us: ["acas", "usa", "security", "flights"] },
        icon: <FaShieldAlt />
    },
    {
        id: "ics2_regulation",
        category: { es: "Alertas", us: "Alerts" },
        title: { es: "Regulación ICS2", us: "ICS2 Regulation" },
        description: { es: "Sistema de control de importaciones de la Unión Europea.", us: "Import control system for the European Union." },
        link: { es: "/alertas/ics2-regulacion", us: "/alerts/ics2-regulation" },
        tags: { es: ["ics2", "europa", "aduana", "importación"], us: ["ics2", "europe", "customs", "import"] },
        icon: <FaShieldAlt />
    },
    {
        id: "pharma_service_alert",
        category: { es: "Noticias", us: "News" },
        title: { es: "Servicio Pharma (Noticia)", us: "Pharma Service (News)" },
        description: { es: "Manejo paso a paso de productos farmacéuticos.", us: "Step-by-step handling of pharmaceuticals." },
        link: { es: "/alertas/venta-azul", us: "/alerts/pharma-service" },
        tags: { es: ["pharma", "noticia", "vacunas"], us: ["pharma", "news", "vaccines"] },
        icon: <FaMedkit />
    },

    // --- COMPANY / ABOUT ---
    {
        id: "about_company",
        category: { es: "Nosotros", us: "About Us" },
        title: { es: "Aeroméxico Cargo", us: "Aeroméxico Cargo" },
        description: { es: "Quiénes somos y nuestra misión logística.", us: "Who we are and our logistics mission." },
        link: { es: "/nosotros/aeromexico-cargo", us: "/about-us/aeromexico-cargo" },
        tags: { es: ["historia", "misión", "visión", "empresa"], us: ["history", "mission", "vision", "company"] },
        icon: <FaBuilding />
    },
    {
        id: "skyteam_cargo",
        category: { es: "Nosotros", us: "About Us" },
        title: { es: "SkyTeam Cargo", us: "SkyTeam Cargo" },
        description: { es: "Alianza global de carga para mayor alcance.", us: "Global cargo alliance for wider reach." },
        link: { es: "/nosotros/skyteam", us: "/about-us/skyteam" },
        tags: { es: ["skyteam", "alianza", "socios", "global"], us: ["skyteam", "alliance", "partners", "global"] },
        icon: <FaGlobe />
    },

    // --- CONTACT / HELP ---
    {
        id: "contact_page",
        category: { es: "Ayuda", us: "Help" },
        title: { es: "Contacto", us: "Contact" },
        description: { es: "Atención al cliente y soporte técnico.", us: "Customer attention and technical support." },
        link: { es: "/contacto", us: "/contact" },
        tags: { es: ["ayuda", "teléfono", "email", "hablar con alguien"], us: ["help", "phone", "email", "talk to someone"] },
        icon: <FaHeadset />
    },
    {
        id: "faqs_page",
        category: { es: "Ayuda", us: "Help" },
        title: { es: "Preguntas Frecuentes (FAQs)", us: "Frequently Asked Questions (FAQs)" },
        description: { es: "Respuestas a dudas comunes sobre trámites.", us: "Answers to common questions about procedures." },
        link: { es: "/faqs", us: "/frequently-asked-questions" },
        tags: { es: ["preguntas", "faqs", "dudas", "soporte"], us: ["questions", "faqs", "support"] },
        icon: <FaQuestionCircle />
    },

    // --- LEGAL / CONTRACTS ---
    {
        id: "contracts_main",
        category: { es: "Legal", us: "Legal" },
        title: { es: "Contratos de Carga", us: "Cargo Contracts" },
        description: { es: "Términos y condiciones legales para tus servicios.", us: "Legal terms and conditions for your services." },
        link: { es: "/contratos", us: "/contracts" },
        tags: { es: ["contratos", "términos", "legal", "condiciones"], us: ["contracts", "terms", "legal", "conditions"] },
        icon: <FaFileSignature />
    },
    {
        id: "national_contract",
        category: { es: "Legal", us: "Legal" },
        title: { es: "Contrato de Carga Nacional", us: "National Cargo Contract" },
        description: { es: "Marco legal para transporte dentro de México.", us: "Legal framework for transport within Mexico." },
        link: { es: "/contrato-de-carga-aerea-nacional", us: "/nationalContract" },
        tags: { es: ["contrato", "nacional", "ley", "condiciones"], us: ["contract", "national", "law"] },
        icon: <FaIdCard />
    },
    {
        id: "international_contract",
        category: { es: "Legal", us: "Legal" },
        title: { es: "Contrato de Carga Internacional", us: "International Cargo Contract" },
        description: { es: "Normativa legal para envíos globales.", us: "Legal regulations for global shipments." },
        link: { es: "/contrato-de-carga-aerea-internacional", us: "/internationalContract" },
        tags: { es: ["contrato", "internacional", "mundo", "términos"], us: ["contract", "international", "world"] },
        icon: <FaIdCard />
    },
    {
        id: "logistics_contract",
        category: { es: "Legal", us: "Legal" },
        title: { es: "Contrato de Servicios Logísticos", us: "Logistics Services Contract" },
        description: { es: "Detalles legales de almacenamiento y logística.", us: "Legal details of warehousing and logistics." },
        link: { es: "/contrato-de-servicios-de-logistica", us: "/logistics-services-contract" },
        tags: { es: ["logística", "almacén", "contrato", "servicios"], us: ["logistics", "warehouse", "contract"] },
        icon: <FaIdCard />
    },
    {
        id: "privacy_policy",
        category: { es: "Legal", us: "Legal" },
        title: { es: "Aviso de Privacidad", us: "Privacy Policy" },
        description: { es: "Protección de datos y trato de información personal.", us: "Data protection and personal information handling." },
        link: { es: "/aviso", us: "/privacy" },
        tags: { es: ["privacidad", "datos", "legal", "protección"], us: ["privacy", "data", "legal", "protection"] },
        icon: <FaLock />
    },
    {
        id: "compliance_page",
        category: { es: "Legal", us: "Legal" },
        title: { es: "Cumplimiento Normativo (Compliance)", us: "Compliance" },
        description: { es: "Estándares éticos y cumplimiento regulatorio.", us: "Ethical standards and regulatory compliance." },
        link: { es: "/cumplimiento-normativo", us: "/compliance" },
        tags: { es: ["ética", "compliance", "normas", "regulación"], us: ["ethics", "compliance", "rules"] },
        icon: <FaShieldAlt />
    },
];
